import React, { useMemo, useState } from 'react'; // Added useState
import { Helmet } from 'react-helmet-async';
import { useFavorites, FavoriteItem } from '../hooks/useFavorites'; // Added FavoriteItem
import { useMoviesByIds } from '../hooks/useMovies';
import { usePersonalMovies } from '../hooks/usePersonalMovies';
import MovieCard from '../components/MovieCard';
import PersonalMovieCard from '../components/PersonalMovieCard';
import AddPersonalMovieDialog from '../components/AddPersonalMovieDialog';
import ErrorBoundary from '../components/ErrorBoundary';
import { FormattedMovie } from '../types/movie';
import { PersonalMovie, CreatePersonalMovieData, UpdatePersonalMovieData } from '../services/personalMoviesService';
import { Link } from 'react-router-dom';
import { FiHeart, FiLoader, FiEye, FiEyeOff, FiPlus } from 'react-icons/fi'; // Added FiPlus

const WatchlistPage = () => {
  const {
    favorites, // Full list of FavoriteItem
    loading: favoritesLoading,
    setWatchedStatus,
    hideWatched,
    setHideWatched,
  } = useFavorites();

  const {
    personalMovies,
    loading: personalMoviesLoading,
    createPersonalMovie,
    updatePersonalMovie,
    deletePersonalMovie,
    refreshPersonalMovies,
    clearCache
  } = usePersonalMovies();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMovie, setEditingMovie] = useState<PersonalMovie | null>(null);

  const favoriteMovieIds = useMemo(() => favorites.map(fav => fav.movie_id), [favorites]);
  const { movies: detailedMovies, loading: moviesDetailsLoading, error } = useMoviesByIds(favoriteMovieIds);

  const moviesToRender = useMemo(() => {
    return detailedMovies
      .map(movie => {
        const favoriteInfo = favorites.find(fav => fav.movie_id === movie._id);
        return {
          ...movie,
          watched: favoriteInfo?.watched ?? false,
        };
      })
      .filter(movie => {
        if (hideWatched) {
          return !movie.watched;
        }
        return true;
      });
  }, [detailedMovies, favorites, hideWatched]);

  const totalFavoriteCount = favorites.length;
  const watchedMovieCount = useMemo(() => favorites.filter(fav => fav.watched).length, [favorites]);

  const handleAddPersonalMovie = () => {
    setEditingMovie(null);
    setIsDialogOpen(true);
  };

  const handleEditPersonalMovie = (movie: PersonalMovie) => {
    setEditingMovie(movie);
    setIsDialogOpen(true);
  };

  const handleSavePersonalMovie = async (data: CreatePersonalMovieData | UpdatePersonalMovieData) => {
    try {
      if (editingMovie) {
        await updatePersonalMovie(editingMovie.id, data as UpdatePersonalMovieData);
      } else {
        await createPersonalMovie(data as CreatePersonalMovieData);
      }
      // Only close dialog on successful save
      setIsDialogOpen(false);
      setEditingMovie(null);
    } catch (error) {
      console.error('Failed to save personal movie:', error);
      // Don't close dialog on error - let user try again
      throw error; // Re-throw so AddPersonalMovieDialog can handle it
    }
  };

  const handleDeletePersonalMovie = async (id: string) => {
    await deletePersonalMovie(id);
  };

  if (favoritesLoading || moviesDetailsLoading || personalMoviesLoading) {
    return (
      <div className="min-h-screen pt-24 bg-background-light flex flex-col items-center justify-center">
        <FiLoader className="w-12 h-12 text-primary animate-spin mb-4" />
        <p className="text-secondary-light">Loading your watchlist movies...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-24 bg-background-light flex flex-col items-center justify-center text-center px-4">
        <FiHeart className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-2xl font-heading font-bold text-secondary-dark mb-2">Error Loading Watchlist</h1>
        <p className="text-secondary-light mb-6">We couldn't load your watchlist movies at this time. Please try again later.</p>
        <pre className="text-xs text-red-400 bg-red-50 p-2 rounded">{error.message}</pre>
      </div>
    );
  }

  return (
    <main className="min-h-screen pt-24 bg-background-light">
      <Helmet>
        <title>My Watchlist - Next in Cinema</title>
        <meta name="description" content="View and manage your watched movies and watchlist." />
      </Helmet>
      <div className="container-custom py-8">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <h1 className="section-title flex items-center gap-3 mb-0">
            <FiHeart className="w-8 h-8 text-primary" />
            My Watchlist
          </h1>
          {totalFavoriteCount > 0 && (
            <label htmlFor="hideWatchedToggle" className="flex items-center gap-2 cursor-pointer text-sm text-secondary-dark hover:text-primary transition-colors">
              <input
                type="checkbox"
                id="hideWatchedToggle"
                checked={hideWatched}
                onChange={(e) => setHideWatched(e.target.checked)}
                className="form-checkbox h-5 w-5 text-primary rounded focus:ring-primary-dark"
              />
              {hideWatched ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
              Hide Watched Movies
            </label>
          )}
        </div>

        {totalFavoriteCount > 0 && (
          <p className="text-sm text-secondary-light mb-6">
            {hideWatched && watchedMovieCount > 0
              ? `Showing ${moviesToRender.length} of ${totalFavoriteCount} movies (${watchedMovieCount} watched ${watchedMovieCount === 1 ? 'movie' : 'movies'} hidden).`
              : `Showing ${totalFavoriteCount} ${totalFavoriteCount === 1 ? 'movie' : 'movies'}${watchedMovieCount > 0 ? ` (${watchedMovieCount} watched)` : ''}.`}
          </p>
        )}

        {/* Official Movies Section */}
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-bold text-secondary-dark mb-6">
            Official Movies
          </h2>
        </div>

        {totalFavoriteCount === 0 ? (
          <div className="text-center py-12">
            <FiHeart className="w-20 h-20 text-secondary-light/30 mx-auto mb-6" />
            <h2 className="text-xl font-semibold text-secondary-dark mb-2">Your Watchlist is Empty!</h2>
            <p className="text-secondary-light mb-6">
              Start exploring and add some movies you want to watch.
            </p>
            <Link to="/" className="btn-primary">
              Discover Movies
            </Link>
          </div>
        ) : moviesToRender.length === 0 && hideWatched ? (
           <div className="text-center py-12">
            <FiEyeOff className="w-20 h-20 text-secondary-light/30 mx-auto mb-6" />
            <h2 className="text-xl font-semibold text-secondary-dark mb-2">All Watched Movies Hidden</h2>
            <p className="text-secondary-light mb-6">
              You've marked all movies in your watchlist as watched. Uncheck "Hide Watched Movies" to see them.
            </p>
            <button onClick={() => setHideWatched(false)} className="btn-secondary">
              Show All Watched Movies
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
            {moviesToRender.map((movie: FormattedMovie & { watched: boolean }) => (
              <MovieCard
                key={movie._id}
                id={movie._id}
                title={movie.title}
                poster={movie.poster}
                releaseDate={movie.releaseDate}
                hypeScore={movie.hypeScore}
                slug={movie.slug}
                watched={movie.watched}
                onToggleWatched={() => setWatchedStatus(movie._id, !movie.watched)}
                showWatchedToggle={true}
              />
            ))}
          </div>
        )}

        {/* Personal Movies Section */}
        <div className="mt-12">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-2xl font-heading font-bold text-secondary-dark flex items-center gap-3">
              <span className="text-2xl">🎬</span>
              Personal Movies
            </h2>
            <button
              onClick={handleAddPersonalMovie}
              className="btn-primary flex items-center gap-2"
            >
              <FiPlus className="w-4 h-4" />
              Add Personal Movie
            </button>
          </div>

          {personalMovies.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <span className="text-4xl mb-4 block">🎭</span>
              <h3 className="text-lg font-semibold text-secondary-dark mb-2">No Personal Movies Yet</h3>
              <p className="text-secondary-light mb-4">
                Create your own custom movie cards with personalized colors and emojis.
              </p>
              <button onClick={handleAddPersonalMovie} className="btn-primary">
                Add Your First Movie
              </button>
            </div>
          ) : (
            <ErrorBoundary
              fallback={
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h3 className="text-red-800 font-semibold mb-2">Error loading personal movies</h3>
                  <p className="text-red-600 text-sm mb-2">
                    There was an error displaying your personal movies. This might be due to corrupted data.
                  </p>
                  <button
                    onClick={() => {
                      clearCache();
                      refreshPersonalMovies();
                    }}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                  >
                    Clear cache and retry
                  </button>
                </div>
              }
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                {personalMovies
                  .filter(movie => movie && movie.id)
                  .map((movie) => (
                    <ErrorBoundary key={movie.id}>
                      <PersonalMovieCard
                        movie={movie}
                        onEdit={handleEditPersonalMovie}
                        onDelete={handleDeletePersonalMovie}
                      />
                    </ErrorBoundary>
                  ))}
              </div>
            </ErrorBoundary>
          )}
        </div>

        {/* Add Personal Movie Dialog */}
        <AddPersonalMovieDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSave={handleSavePersonalMovie}
          editingMovie={editingMovie}
        />
      </div>
    </main>
  );
};

export default WatchlistPage;